import React from 'react';
import type { Medication } from '../../../../../dtos/patientmr/medication.dto';
import { MAR_EFFECTIVENESS, MAR_STATUSES, MAR_WITHHOLD_REASONS } from '../../../../../dtos/patientmr/charting/medication.dto';
import type { MarStatus } from '../../../../../dtos/patientmr/charting';
import type { Entry, MarFormData } from '../../types';
import MarMedicationFields from '../MarMedicationFields';
import { isPrnMedication, REQUIRED } from '../../model/entryValidation';
import { clockLabel, codeLabel } from '../../model/entryMeta';
import { FieldsProps, InputField, Segmented, SelectField, TextAreaField } from './fieldParts';

export const MAR_STATUS_LABELS: Record<MarStatus, string> = { GIVEN: 'Given', HELD: 'Held', REFUSED: 'Refused', NOT_GIVEN: 'Not given' };
export const WITHHOLD_REASON_LABELS: Record<string, string> = {
  NPO: 'NPO',
  VOMITING: 'Vomiting',
  PARAMETER: 'Outside ordered parameters',
  MD_ORDER: 'MD order',
  REFUSED: 'Refused',
  UNAVAILABLE: 'Medication unavailable',
  OTHER: 'Other (explain in notes)',
};
export const EFFECTIVENESS_LABELS: Record<string, string> = { EFFECTIVE: 'Effective', PARTIAL: 'Partly effective', NOT_EFFECTIVE: 'Not effective' };

// Phase 18U U12: touch sizing follows isMobile (phones and field-mode tablets), not the width
const inputFor = (isMobile: boolean) => `px-2 border rounded border-gray-300 focus:border-blue-500 focus:outline-none ${isMobile ? 'py-2.5 min-h-[44px] text-base' : 'py-1 text-sm'}`;
const LABEL = 'text-xs font-medium text-green-700';

/**
 * Phase 18 M7 MAR form: status (Given / Held / Refused / Not given), reason unless Given
 * (Other needs notes), PRN indication, and the PRN follow-up ("Record effect").
 */
const MarFields: React.FC<FieldsProps<MarFormData> & {
  medications: Medication[];
  lastGivenByMedication?: Record<string, string>;
  /** The PRN GIVEN entry this form follows up (followUpOf) */
  followUpEntry?: Entry;
}> = ({ value, onChange, errors, showRequired, isMobile, disabled, medications, lastGivenByMedication = {}, followUpEntry }) => {
  const set = (field: keyof MarFormData) => (v: string) => onChange({ ...value, [field]: v });
  const common = { isMobile, disabled, showRequired, labelClassName: 'text-green-700' };
  const prn = isPrnMedication(value.medicationId, medications);
  const isFollowUp = Boolean(value.followUpOf);
  const medError = errors.medicationId && (errors.medicationId !== REQUIRED || showRequired) ? errors.medicationId : undefined;

  const time = (
    <InputField {...common} id="mar-administration-time" type="time"
      label={isFollowUp ? 'Assessed at (observed at)' : 'Administration time (observed at)'}
      value={value.administrationTime} onChange={set('administrationTime')} error={errors.administrationTime} />
  );
  const notes = (
    <TextAreaField {...common} id="mar-notes" label="Notes" rows={isMobile ? 4 : 3}
      placeholder={isFollowUp ? 'Response observed…' : 'Response, observations, administration notes...'}
      value={value.notes} onChange={set('notes')} error={errors.notes} />
  );

  if (isFollowUp) {
    const givenAt = followUpEntry ? clockLabel(followUpEntry.observedAt || followUpEntry.date) : '';
    return (
      <div className="space-y-2" data-testid="mar-follow-up">
        <p className="rounded border border-green-200 bg-white/70 px-2 py-1 text-xs text-green-900">
          Effect of PRN {value.medicationName || 'medication'}{givenAt ? ` given at ${givenAt}` : ''}
        </p>
        <Segmented name="mar-effectiveness" label="Effect" required isMobile={isMobile} disabled={disabled}
          options={MAR_EFFECTIVENESS.map((e) => ({ value: e, label: EFFECTIVENESS_LABELS[e] }))}
          value={value.effectiveness} onChange={set('effectiveness')} error={errors.effectiveness} showRequired={showRequired} />
        {time}
        {notes}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <MarMedicationFields
        marData={value}
        setMarData={onChange}
        medications={medications}
        disabled={disabled}
        inputClassName={inputFor(isMobile)}
        labelClassName={LABEL}
        lastGivenByMedication={lastGivenByMedication}
        searchable={isMobile}
        medicationError={medError || (errors.medicationName && showRequired ? errors.medicationName : undefined)}
        doseFields={
          <div className="grid grid-cols-2 gap-2">
            <InputField {...common} id="mar-dose-amount" label="Dose Amount" placeholder="e.g. 500" value={value.doseAmount} onChange={set('doseAmount')} />
            <InputField {...common} id="mar-dose-unit" label="Unit" placeholder="e.g. mg" value={value.doseUnit} onChange={set('doseUnit')} />
          </div>
        }
      />
      <Segmented name="mar-status" label="Status" required isMobile={isMobile} disabled={disabled}
        options={MAR_STATUSES.map((s) => ({ value: s, label: MAR_STATUS_LABELS[s] }))}
        value={value.status}
        onChange={(status) => onChange({
          ...value,
          status: status as MarStatus,
          // A refusal is its own reason; Given clears the reason
          withholdReason: status === 'GIVEN' ? '' : status === 'REFUSED' && !value.withholdReason ? 'REFUSED' : value.withholdReason,
        })} />
      {value.status !== 'GIVEN' && (
        <SelectField {...common} id="mar-withhold-reason" label="Reason" required
          options={MAR_WITHHOLD_REASONS.map((r) => ({ value: r, label: WITHHOLD_REASON_LABELS[r] || codeLabel(r) }))}
          value={value.withholdReason} onChange={set('withholdReason')} error={errors.withholdReason} />
      )}
      {prn && (
        <InputField {...common} id="mar-indication" label="PRN indication" required placeholder="e.g. temp 101.2 °F, wheezing"
          value={value.indication} onChange={set('indication')} error={errors.indication} />
      )}
      {value.status === 'GIVEN' && (
        <div className="grid grid-cols-2 gap-2">
          <InputField {...common} id="mar-site" label="Site" placeholder="e.g. left thigh" value={value.site} onChange={set('site')} />
          <InputField {...common} id="mar-reaction" label="Reaction" placeholder="e.g. none" value={value.reaction} onChange={set('reaction')} />
        </div>
      )}
      {time}
      {notes}
    </div>
  );
};

export default MarFields;
